import {
  POINT_SHADOW_BIAS,
  SHADOW_MAP_SIZE,
  SPOT_CAMERA_FAR,
  SPOT_CAMERA_FOCUS,
  SPOT_CAMERA_FOV,
  SPOT_CAMERA_NEAR,
} from '@constants'
import { useFrame } from '@react-three/fiber'
import { useAppStore } from '@store'
import { LightProps } from '@types'
import { radians } from '@utils/generalMaths/geometry'
import { resolveSceneLightLocalY } from '@utils/sceneObjects/lights/resolveSceneLightLocalY'
import { useMemo, useRef } from 'react'
import { Quaternion, SpotLight, Vector3 } from 'three'
import { LightPivot } from '../../ViewerManager/LightPivot'
import { LightMesh3D } from './LightMesh3D'

export function SpotLight3D({ id, z, textures, color, catalogID, matrix }: LightProps) {
  const { castShadow, angle, penumbra, distance } = textures
  const power = (textures as any)?.power
  const light = useAppStore.use.items().get(id as any)
  const intensity = (light as any)?.intensity
  const items = useAppStore.use.items()

  const hasWalls = Array.from(items.values()).some((item) => item.type === 'walls')

  let baseIntensity = intensity ? (intensity * 30) / 100 : 0
  if (!hasWalls && catalogID === 'Luzgeneral2') {
    baseIntensity = baseIntensity * 0.45
  }
  const calcIntensity = baseIntensity

  const x = (light as any)?.width || 10
  const validX = isNaN(x) || !isFinite(x) ? 10 : x
  const targetX = validX / 2
  const targetY = -10
  const targetZ = validX / 2
  const validTargetX = isNaN(targetX) || !isFinite(targetX) ? 5 : targetX
  const validTargetY = isNaN(targetY) || !isFinite(targetY) ? -10 : targetY
  const validTargetZ = isNaN(targetZ) || !isFinite(targetZ) ? 5 : targetZ

  const spotlight = useMemo(() => new SpotLight(), [])
  const ref = useRef<SpotLight>(null)

  useFrame(() => {
    if (!spotlight || !matrix) return

    const pivotPosition = new Vector3()
    const pivotQuaternion = new Quaternion()
    const pivotScale = new Vector3()
    matrix.decompose(pivotPosition, pivotQuaternion, pivotScale)

    const lightLocalY = resolveSceneLightLocalY(pivotPosition.y, hasWalls)
    spotlight.position.set(validX / 2, lightLocalY, validX / 2)
    spotlight.target.position.set(validTargetX, validTargetY, validTargetZ)
    spotlight.target.updateMatrixWorld()
  })

  return (
    <LightPivot
      pivotKey={`${validX}-${z}-${id}`}
      id={id}
      matrix={matrix}
    >
      <primitive
        object={spotlight}
        name={id}
        ref={ref}
        power={(power as any)?.value ?? 0.4}
        intensity={calcIntensity}
        castShadow={castShadow.value}
        color={color}
        decay={0}
        angle={radians(angle.value / 2)}
        penumbra={penumbra.value}
        distance={distance.value}
        shadow-mapSize={SHADOW_MAP_SIZE}
        shadow-camera-far={SPOT_CAMERA_FAR}
        shadow-camera-near={SPOT_CAMERA_NEAR}
        shadow-camera-fov={SPOT_CAMERA_FOV}
        shadow-camera-focus={SPOT_CAMERA_FOCUS}
        shadow-bias={POINT_SHADOW_BIAS}
      />
      <primitive object={spotlight.target} />
      <LightMesh3D
        id={id}
        catalogID={catalogID}
      />
    </LightPivot>
  )
}
